<!--
  Sign-in page.

  Two states in one card: ask for an address, then ask for the 6-digit code we
  emailed to it. A direct port of login.js, including every refusal it knew how
  to describe.

  The endpoints:
    POST /v1/auth/email  {email}        -> {sent: true, expires_in: 600}
    POST /v1/auth/verify {email, code}  -> sets the session cookie
    GET  /auth/google?popup=1           -> a redirect into a window of its own
    GET  /auth/google                   -> a redirect; we just navigate
                                           (the fallback when a popup is refused)
    GET  /v1/auth/me                    -> the principal, plus whether Google
                                           sign-in is configured at all
-->
<script lang="ts">
  import { onDestroy, onMount, tick } from 'svelte'
  import { me } from '../lib/me'
  import { safeNextPath } from '../lib/safe'
  import { clearBounce } from '../lib/api'
  import { router } from '../lib/router.svelte'
  import AuthErrorNotice from '../lib/components/AuthErrorNotice.svelte'
  import LoginSketch from '../lib/components/LoginSketch.svelte'
  import TopBar from '../lib/components/TopBar.svelte'
  import { inview } from '../lib/inview'
  import type { Component } from 'svelte'

  /** Seconds to wait before "Resend code" is offered again, absent a 429. */
  const RESEND_COOLDOWN = 30

  /*
   * TASK-047. unomena.com's companion dot, on this page and no other.
   *
   * Loaded with a dynamic import for two reasons, and the second is the one
   * that matters. The first is size: it is ~30 KB of source, and the document
   * list and the editor have no use for a single byte of it. The second is
   * that the import IS the fallback -- on a touch screen, or for somebody who
   * asked for less motion, the chunk is never fetched at all, so there is no
   * dot element, no hollowed full stops, and every heading on the page keeps
   * the plain red one it renders with. That is the same bargain the site
   * strikes; it just strikes it one layer earlier, where it costs nothing.
   *
   * The component runs the identical test again in its own onMount. Belt and
   * braces on purpose: the test there is the vendored code, untouched, and
   * removing it to avoid repeating it here would be a change to the copy.
   */
  let Dot = $state<Component<Record<string, never>> | null>(null)

  /*
   * And unomena.com's footer, on the same terms but with no gate in front of
   * it -- everybody gets the footer.
   *
   * It is imported rather than mounted directly for the reason the dot is: it
   * is furniture for this one public page, and the document list and the
   * editor have no footer at all. Together the two of them put 2.3 KB gzipped
   * into the main chunk, over the 2 KB this ticket allows; apart, the main
   * chunk grows by 0.9 KB and the two of them are 5.6 KB that only a visitor
   * to the sign-in page ever fetches.
   *
   * It cannot shift the page: the footer begins roughly 2,000 px below the
   * fold at 1280x800 and further down at 390x844, so on every width this page
   * supports it arrives outside the viewport, under content that does not
   * move. Nothing above it is laid out relative to it. See test 7 in
   * e2e/tests/login-dot.spec.ts, which measures exactly that.
   */
  let Footer = $state<Component<Record<string, never>> | null>(null)

  let step = $state<'email' | 'code'>('email')
  let googleAvailable = $state(false)

  let email = $state('')
  let code = $state('')
  let address = $state('')
  let sentNote = $state('')

  let busy = $state(false)
  let emailLabel = $state('Email me a sign-in code')
  let codeLabel = $state('Sign in')
  let resendLabel = $state('Resend code')
  let resendDisabled = $state(false)

  let statusMsg = $state('')
  let errorMsg = $state('')

  let lastTriedCode = ''
  let cooldownTimer: ReturnType<typeof setInterval> | null = null

  let emailInput = $state<HTMLInputElement | null>(null)
  let codeInput = $state<HTMLInputElement | null>(null)

  onDestroy(() => {
    if (cooldownTimer) clearInterval(cooldownTimer)
    removeEventListener('message', onAuthMessage)
    if (googleTimeout) clearTimeout(googleTimeout)
    authChannel?.close()
    authChannel = null
  })

  // Listening from the start, not from the click: the popup is a separate
  // page with its own timing, and one that finished early would otherwise
  // shout at nobody.
  onMount(() => {
    addEventListener('message', onAuthMessage)
    try {
      authChannel = new BroadcastChannel(AUTH_CHANNEL)
      authChannel.onmessage = (e) => applyAuthOutcome(e.data as AuthOutcome | null)
    } catch {
      // No BroadcastChannel: the opener postMessage above is the fallback,
      // and failing that the popup still signed the person in -- they land
      // on the app the next time anything navigates.
      authChannel = null
    }
  })

  /* ---------------------------------------------------------- navigation */

  function nextPath(): string {
    return safeNextPath(router.query.get('next'))
  }

  function goNext() {
    // replace(), not assign(): the back button should not return to a sign-in
    // page the person has already completed. A full navigation, not a client
    // route change, so the new session cookie is presented from the start.
    location.replace(nextPath())
  }

  /* ------------------------------------------------------------ messages */

  function clearMessages() {
    statusMsg = ''
    errorMsg = ''
  }

  function say(text: string) {
    errorMsg = ''
    statusMsg = text
  }

  function fail(text: string) {
    statusMsg = ''
    errorMsg = text
  }

  /* --------------------------------------------------------------- fetch */

  interface Result {
    offline: boolean
    ok: boolean
    status: number
    body: any
    retryAfter: number
  }

  /**
   * A POST that never throws for an HTTP status, and never throws a raw
   * TypeError at the caller either. `offline` is the network itself failing --
   * the one failure where retrying the same thing is the right advice.
   */
  async function post(path: string, payload: unknown): Promise<Result> {
    let res: Response
    try {
      res = await fetch(path, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json', accept: 'application/json' },
        body: JSON.stringify(payload),
      })
    } catch {
      return { offline: true, ok: false, status: 0, body: null, retryAfter: 0 }
    }
    const text = await res.text().catch(() => '')
    let body: any = null
    try {
      body = JSON.parse(text)
    } catch {
      /* a refusal that is not JSON still has a status worth reporting */
    }
    return { offline: false, ok: res.ok, status: res.status, body, retryAfter: retryAfter(res, body) }
  }

  /**
   * How long until this caller may try again.
   *
   * The contract does not pin the shape down, so all the usual spellings are
   * accepted: the standard Retry-After header first, then a few body fields.
   * Zero means "we were not told", and the UI then says so rather than
   * inventing a number.
   */
  function retryAfter(res: Response, body: any): number {
    const header = Number(res.headers.get('retry-after'))
    if (Number.isFinite(header) && header > 0) return Math.ceil(header)
    const candidates = [
      body && body.retry_after,
      body && body.retry_after_seconds,
      body && body.retryAfter,
      body && body.data && body.data.retry_after,
    ]
    for (const value of candidates) {
      const n = Number(value)
      if (Number.isFinite(n) && n > 0) return Math.ceil(n)
    }
    return 0
  }

  function inWords(seconds: number): string {
    if (!seconds) return 'in a moment'
    if (seconds < 60) return `in ${seconds} second${seconds === 1 ? '' : 's'}`
    const mins = Math.ceil(seconds / 60)
    return `in ${mins} minute${mins === 1 ? '' : 's'}`
  }

  /** The refusal's own words, when it has any worth showing. */
  function serverSays(result: Result): string {
    const body = result.body
    const message = body && typeof body.message === 'string' ? body.message.trim() : ''
    return message && message.length <= 200 ? message : ''
  }

  function refusalCode(result: Result): string {
    const body = result.body || {}
    return String(body.code || body.error || '').toLowerCase()
  }

  /* ------------------------------------------------------- Google, or not */

  /**
   * Whether Google sign-in is actually wired up on this deployment.
   *
   * The flag's name is not pinned down by the contract, so several plausible
   * spellings are read. The default is *false*: the first deploy ships without
   * Google credentials, and a button that bounces the person off to a broken
   * consent screen is worse than an email-only page.
   */
  function googleConfigured(body: any): boolean {
    if (!body || typeof body !== 'object') return false
    const providers = body.providers || {}
    const flags = [
      body.google_enabled,
      body.googleEnabled,
      body.google_configured,
      body.googleConfigured,
      providers.google,
      body.google && typeof body.google === 'object' ? body.google.configured : body.google,
    ]
    return flags.some((flag) => flag === true)
  }

  /** Whether that same response describes somebody already signed in. */
  function alreadySignedIn(body: any): boolean {
    if (!body || typeof body !== 'object') return false
    const principal = body.principal || body.user || body
    if (!principal || typeof principal !== 'object') return false
    return Boolean(principal.id || principal.email || principal.sub || principal.token_id)
  }

  /**
   * One call at load: it answers both "are you already signed in" and "should
   * the Google button exist". The flag is read whatever the status, because the
   * people who need the button are exactly the ones /v1/auth/me will refuse.
   */
  async function probe() {
    // `lib/me.ts` owns this call now. TASK-042 §4: two callers of one endpoint,
    // each with its own idea of the answer's shape, is how the second one ends
    // up reading a field the first one renamed. `me()` answers `null` when the
    // server is unreachable -- leave Google hidden and let the person try the
    // email form, which reports the same failure in its own words.
    const answer = await me()
    if (!answer) return
    const body = answer.raw
    // `authenticated: false` comes back with a 200 and a null user, so the
    // principal check below is what separates the two.
    if (answer.authenticated && alreadySignedIn(body)) {
      goNext()
      return
    }
    googleAvailable = googleConfigured(body)
  }

  /* --------------------------------------------------------------- steps */

  /** A resend that is honest about when it will work again. */
  function startCooldown(seconds: number) {
    if (cooldownTimer) clearInterval(cooldownTimer)
    let left = Math.max(0, Math.ceil(seconds))
    const tick = () => {
      if (left <= 0) {
        if (cooldownTimer) clearInterval(cooldownTimer)
        cooldownTimer = null
        resendDisabled = false
        resendLabel = 'Resend code'
        return
      }
      resendDisabled = true
      resendLabel = `Resend in ${left}s`
      left -= 1
    }
    tick()
    cooldownTimer = setInterval(tick, 1000)
  }

  function looksLikeEmail(value: string): boolean {
    // Deliberately loose. The server is the authority on what it will deliver
    // to; this only catches the empty box and the missing @.
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
  }

  async function sendCode(value: string, resending = false): Promise<boolean> {
    const result = await post('/v1/auth/email', { email: value })

    if (result.offline) {
      fail('We could not reach the server. Check your connection and try again.')
      return false
    }
    if (result.status === 429) {
      const wait = result.retryAfter
      fail(`Too many sign-in emails for now. Try again ${inWords(wait)}.`)
      startCooldown(wait || RESEND_COOLDOWN)
      return false
    }
    if (result.status === 400 || result.status === 422) {
      fail(serverSays(result) || "That doesn't look like an email address we can use.")
      return false
    }
    if (result.status === 403 && refusalCode(result).includes('signup_not_permitted')) {
      fail(
        serverSays(result) ||
          'That email address is not allowed to sign up on this server. Ask an administrator to add your domain.',
      )
      return false
    }
    if (!result.ok) {
      fail(serverSays(result) || `Something went wrong at our end (${result.status}). Please try again.`)
      return false
    }

    address = value
    const expires = Number(result.body && result.body.expires_in)
    const life = Number.isFinite(expires) && expires > 0 ? Math.round(expires / 60) : 0

    sentNote = life
      ? `Check your email — we sent a 6-digit code to ${address}. It expires in ${life} minute${life === 1 ? '' : 's'}.`
      : `Check your email — we sent a 6-digit code to ${address}.`

    say(resending ? `A new code is on its way to ${address}.` : `We sent a code to ${address}.`)
    showCodeStep()
    startCooldown(RESEND_COOLDOWN)
    return true
  }

  function showCodeStep() {
    step = 'code'
    lastTriedCode = ''
    code = ''
    queueMicrotask(() => codeInput?.focus())
  }

  function showEmailStep() {
    if (cooldownTimer) clearInterval(cooldownTimer)
    cooldownTimer = null
    step = 'email'
    clearMessages()
    queueMicrotask(() => {
      emailInput?.focus()
      emailInput?.select()
    })
    // Google comes back only if it was ever there; probe() owns that decision.
    probe().catch(() => {})
  }

  /* -------------------------------------------------------------- verify */

  function describeVerifyFailure(result: Result): string {
    if (result.offline) return 'We could not reach the server. Check your connection and try again.'

    const rcode = refusalCode(result)
    const words = serverSays(result).toLowerCase()
    const mentions = (needle: string) => rcode.includes(needle) || words.includes(needle)

    if (result.status === 429 || mentions('too_many') || mentions('too many') || mentions('attempt')) {
      return `Too many attempts. Request a new code and try again ${inWords(result.retryAfter)}.`
    }
    if (mentions('expired') || mentions('expire')) {
      return 'That code has expired. Ask for a new one and we will send a fresh code.'
    }
    if ([400, 401, 403, 422].includes(result.status)) {
      return "That code isn't right. Check the email and try again."
    }
    return serverSays(result) || `Something went wrong at our end (${result.status}). Please try again.`
  }

  async function verify(value: string) {
    lastTriedCode = value
    busy = true
    codeLabel = 'Signing in…'
    clearMessages()

    const result = await post('/v1/auth/verify', { email: address, code: value })

    busy = false
    codeLabel = 'Sign in'

    if (result.ok) {
      say('Signed in. Taking you through…')
      goNext()
      return
    }

    fail(describeVerifyFailure(result))
    queueMicrotask(() => {
      codeInput?.focus()
      codeInput?.select()
    })
  }

  /* -------------------------------------------------------------- wiring */

  async function onEmailSubmit(event: Event) {
    event.preventDefault()
    if (busy) return
    const value = email.trim()
    if (!looksLikeEmail(value)) {
      fail('Enter an email address so we know where to send the code.')
      emailInput?.focus()
      return
    }
    clearMessages()
    busy = true
    emailLabel = 'Sending…'
    await sendCode(value)
    busy = false
    emailLabel = 'Email me a sign-in code'
  }

  function onCodeSubmit(event: Event) {
    event.preventDefault()
    if (busy) return
    const value = code.replace(/\D/g, '')
    if (value.length !== 6) {
      fail('Enter the 6-digit code from the email.')
      codeInput?.focus()
      return
    }
    verify(value)
  }

  /**
   * iOS and Android drop the whole code in at once from the email. Waiting for
   * a deliberate tap on "Sign in" after an autofill that already filled the
   * field is a step with nothing in it -- so a complete code submits itself,
   * once.
   */
  function onCodeInput() {
    const digits = code.replace(/\D/g, '').slice(0, 6)
    if (code !== digits) code = digits
    if (digits.length === 6 && !busy && digits !== lastTriedCode) verify(digits)
  }

  async function onResend() {
    if (busy || resendDisabled || !address) return
    clearMessages()
    busy = true
    resendDisabled = true
    resendLabel = 'Sending…'
    await sendCode(address, true)
    busy = false
    // startCooldown() owns the button's label and disabled state from here.
    if (!cooldownTimer) {
      resendDisabled = false
      resendLabel = 'Resend code'
    }
  }

  /* ------------------------------------------------- Google, in a popup ---
   *
   * Euan asked for the shape claude.ai uses: the page stays where it is, the
   * Google business happens in a window of its own, and when that window is
   * done the page underneath signs in and moves on. The whole-page redirect is
   * still here and still correct, because a popup is a thing a browser is
   * allowed to refuse.
   *
   * Why this works at all: the popup's last request is to OUR origin
   * (/auth/callback/google), and that response sets the session cookie. A
   * cookie belongs to the origin, not to the window that earned it, so by the
   * time the popup speaks the opener is already signed in. All the message
   * carries is "go", and where to.
   *
   * THE CHANNEL IS NOT window.opener. Google's sign-in pages send
   * `Cross-Origin-Opener-Policy: same-origin`; navigating the popup to Google
   * puts it in a new browsing-context group and severs the opener link
   * permanently. Two things follow, both measured rather than assumed:
   * `window.opener` is null in the popup when it returns, and the handle this
   * page kept reports `closed === true` roughly a second after the popup
   * reaches Google, while the window is plainly still open. So the outcome
   * arrives on a BroadcastChannel -- scoped to the origin, which both pages
   * share -- and NOTHING here trusts `googleWindow.closed`.
   *
   * What is not trusted either: the message. Same-origin by construction on
   * the channel, checked for origin and our own marker on the postMessage
   * path, and the destination goes through safeNextPath again even though
   * this server sealed it.
   */
  const GOOGLE_POPUP = { width: 480, height: 640 }
  const AUTH_CHANNEL = 'unomena-docs-auth'

  /**
   * Where to put the window: the middle of the SCREEN THIS WINDOW IS ON.
   *
   * Left to itself a popup lands in the top-left corner, which on a wide
   * display is nowhere near what the person is looking at. Centring it on
   * `screen` alone is the usual fix and the usual bug: on a second monitor
   * `screen.width` still describes the primary one, so the window opens on
   * the wrong display. `screenLeft`/`screenTop` say where THIS window is, and
   * `outerWidth`/`outerHeight` how big it is, so the sum is the middle of the
   * browser window wherever it happens to be.
   *
   * Clamped to this window's own bounds, so a browser window narrower or
   * shorter than the popup pins it to that window's corner instead of
   * centring it to a negative offset -- and floored at zero, because a
   * negative coordinate is refused on some platforms and silently honoured on
   * others.
   */
  function centredOn(width: number, height: number): string {
    const winLeft = window.screenLeft ?? window.screenX ?? 0
    const winTop = window.screenTop ?? window.screenY ?? 0
    const winWidth = window.outerWidth || document.documentElement.clientWidth || width
    const winHeight = window.outerHeight || document.documentElement.clientHeight || height
    const maxLeft = winLeft + Math.max(0, winWidth - width)
    const maxTop = winTop + Math.max(0, winHeight - height)
    const left = Math.round(Math.min(maxLeft, Math.max(winLeft, winLeft + (winWidth - width) / 2)))
    const top = Math.round(Math.min(maxTop, Math.max(winTop, winTop + (winHeight - height) / 2)))
    return `popup=1,width=${width},height=${height},left=${Math.max(0, left)},top=${Math.max(0, top)}`
  }
  /*
   * How long the button is willing to say "Waiting for Google". Only a label:
   * the round trip is not cancelled and a message arriving later is still
   * acted on. It exists because a person who closes the window can no longer
   * be detected (see above), and a button that waits for ever is a bug.
   */
  const GOOGLE_GIVE_UP_MS = 4 * 60 * 1000

  let googleWaiting = $state(false)
  let googleWindow: Window | null = null
  let googleTimeout: ReturnType<typeof setTimeout> | null = null
  let authChannel: BroadcastChannel | null = null

  function stopWaitingForGoogle() {
    if (googleTimeout) {
      clearTimeout(googleTimeout)
      googleTimeout = null
    }
    googleWindow = null
    googleWaiting = false
  }

  type AuthOutcome = { source?: string; ok?: boolean; next?: string; error?: string | null }

  function applyAuthOutcome(data: AuthOutcome | null) {
    if (!data || data.source !== AUTH_CHANNEL) return
    stopWaitingForGoogle()
    if (data.ok) {
      // A full load, not a client-side route change: the session is new and
      // every piece of state on this page was built for a signed-out visitor.
      location.assign(safeNextPath(data.next ?? '/') ?? '/')
    } else {
      // The same notice the redirect path raises, said the same way.
      router.setQuery('auth_error', data.error || 'google_failed')
    }
  }

  /** The opener path, for the case where COOP did not sever it. */
  function onAuthMessage(event: MessageEvent) {
    if (event.origin !== location.origin) return
    applyAuthOutcome(event.data as AuthOutcome | null)
  }

  function onGoogle() {
    const next = nextPath()
    // Opened synchronously inside the click. Anything awaited first and the
    // pop-up blocker is within its rights to eat the window.
    const opened = window.open(
      `/auth/google?popup=1&next=${encodeURIComponent(next)}`,
      'unomena-docs-google',
      centredOn(GOOGLE_POPUP.width, GOOGLE_POPUP.height),
    )
    if (!opened) {
      // Refused. Fall back to the round trip that leaves the page, which is
      // what this button did before and needs nothing of the browser.
      location.assign(`/auth/google?next=${encodeURIComponent(next)}`)
      return
    }
    clearMessages()
    router.dropQuery('auth_error')
    googleWindow = opened
    googleWaiting = true
    // A second click while waiting reuses the window by name and brings it
    // back to the front, which is what somebody who lost it behind the
    // browser actually wants. That is why the button is not disabled.
    opened.focus?.()
    if (googleTimeout) clearTimeout(googleTimeout)
    googleTimeout = setTimeout(stopWaitingForGoogle, GOOGLE_GIVE_UP_MS)
  }

  /**
   * The bottom of the page sends you back to the top of it.
   *
   * A second form down there would be a second thing to keep in step with the
   * first -- two code fields, two rate-limit states -- so this is a button
   * that moves the cursor into the ONE form, at whatever step that form has
   * reached: somebody who read to the bottom while waiting for the email
   * wants the code box, not the address box they already filled in.
   *
   * Scroll first, focus second, and `preventScroll` on the focus: the bar is
   * sticky, and the browser's own "scroll the focused thing into view" puts
   * the field's top edge exactly under it.
   */
  function toForm() {
    const field = step === 'code' ? codeInput : emailInput
    field?.scrollIntoView({ block: 'center' })
    field?.focus({ preventScroll: true })
  }

  // Once, at mount. An $effect here would re-run every time the input element
  // is swapped between the two steps, re-probing for no reason.
  onMount(() => {
    /*
     * The ping-pong guard counts bounces between here and a page that refused
     * us. Arriving HERE means the bounce ended, so the count starts again --
     * nothing cleared it before, so two bounces in a tab left the guard tripped
     * for the rest of the session and the next visit was handed the app with
     * an empty list and a message about signing in. Euan, 2026-09-20.
     */
    clearBounce()
    // `preventScroll`, or the page opens scrolled past the headline it exists
    // to show: on a phone the card sits below the H1, and focusing a field
    // that is below the fold scrolls it into view by default.
    emailInput?.focus({ preventScroll: true })
    probe().catch(() => {})

    /*
     * The site's own test, verbatim: a device with a real pointer that can
     * hover, belonging to somebody who has not asked for less motion. Anything
     * else gets no dot, which is not a degraded page -- the full stops are
     * simply full stops, which is what they are on the site too.
     */
    const wanted =
      matchMedia('(hover: hover) and (pointer: fine)').matches &&
      !matchMedia('(prefers-reduced-motion: reduce)').matches
    if (!wanted) return

    // Failure is silence: a chunk that does not arrive costs the page a
    // decoration, and there is nothing to tell anybody about it.
    import('../lib/components/TravelingDot.svelte')
      .then((m) => {
        Dot = m.default as Component<Record<string, never>>
      })
      .catch(() => {})
  })

  // Ungated, and first: the dot is optional, the footer is not.
  onMount(() => {
    import('../lib/components/SiteFooter.svelte')
      .then(async (m) => {
        Footer = m.default as Component<Record<string, never>>
        /*
         * The two chunks race, and the dot scans the document once when it
         * mounts. If the footer lost that race its wordmark would never join
         * the itinerary and the journey would have no terminus.
         *
         * `tdot-rescan` is the vendored component's own answer to exactly
         * this -- the site fires it when its contact form swaps a success
         * heading in without a navigation. Dispatched after `tick()`, so the
         * footer is really in the DOM when the scan runs, and harmless when
         * the dot is not there to hear it (a touch screen, reduced motion, or
         * simply the slower of the two chunks -- in which case its own first
         * scan finds the footer already in place).
         */
        await tick()
        dispatchEvent(new CustomEvent('tdot-rescan'))
      })
      .catch(() => {})
  })
</script>

<svelte:head><title>Unomena Docs · Documents your agents write with you</title></svelte:head>

<!--
  The site's own bar: this screen is part of unomena.com, and arriving at it
  should not feel like leaving. `bare` puts "Back" where the app keeps Sign
  out -- the only place worth going from a page you are not signed in to.
-->
<TopBar bare />

<!--
  TASK-046. This is not a sign-in screen with an essay bolted on; it is a page
  of unomena.com that happens to contain the way in. A stranger arriving from
  the site's products section has to learn, without scrolling, what the thing
  is, that it costs nothing, and where to start.

  The order is deliberate and is NOT the same on both widths:

    wide   the words on the left, the card on the right, one grid
    phone  kicker, headline, CARD, then the lede under it

  On a phone the card cannot come first -- it would open on a form titled
  "Start writing" with nothing saying what you would be writing -- and it
  cannot come last either, because the submit button has to be inside 844px.
  Above the card it is 68 (bar) + the hero's pad + the kicker + the headline,
  which puts the button's bottom edge at 607px of 844 (707 if a deployment has
  Google configured and the card grows by that button). The hero's top padding is
  therefore this page's own (clamp(2rem, 6vw, 5rem)), not the site's taller
  clamp(4rem, 9vw, 8rem), and the headline is --text-display, never -xl: at
  -xl the headline alone is five lines at 1280 and there is no fold left.
-->
<main class="product">
  <section class="hero">
    <p class="meta-label kicker rise-fade">Unomena Docs · Free to use</p>

    <h1 class="hero-title rise">Documents your agents write with you<span class="dot">.</span></h1>

    <!--
      The card does not animate. Everything else on the page rises into place;
      the one thing a person came here to type into is simply there, at full
      opacity, from the first frame -- and it is what has focus.
    -->
    <div class="card">
      <AuthErrorNotice />

      <!--
        An h2, not an h1. The page's one H1 is the product headline above; two
        H1s leave a screen-reader user with two competing answers to "what is
        this page". TASK-034.

        "We email you a code and a link", not "a code or a Google account":
        Google is unconfigured in production and the button below is
        conditional on /v1/auth/me saying otherwise. The card must not promise
        a way in that will not be there.
      -->
      <h2 class="card-title">Start writing</h2>
      <p class="card-note">No password. We email you a code and a link.</p>

      {#if step === 'email'}
        <!--
          Hidden until /v1/auth/me confirms Google is configured. The first deploy
          ships without credentials, and a button that dead-ends is worse than no
          button at all. The Google mark is inline SVG rather than a remote image,
          so no img-src exception is needed.
        -->
        {#if googleAvailable}
          <button
            type="button"
            class="btn btn-block btn-provider"
            onclick={onGoogle}
          >
            <svg class="provider-mark" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
              <path
                fill="#4285F4"
                d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"
              />
              <path
                fill="#34A853"
                d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"
              />
              <path
                fill="#FBBC05"
                d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34A21.99 21.99 0 0 0 2 24c0 3.55.85 6.91 2.34 9.88l7.35-5.7z"
              />
              <path
                fill="#EA4335"
                d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"
              />
            </svg>
            <span>{googleWaiting ? 'Waiting for Google…' : 'Continue with Google'}</span>
          </button>
  
          <div class="auth-or"><span>OR</span></div>
        {/if}
  
        <form onsubmit={onEmailSubmit} novalidate>
          <div class="field">
            <label class="field-label" for="email">Email <span class="req">*</span></label>
            <input
              class="input"
              id="email"
              name="email"
              type="email"
              autocomplete="email"
              autocapitalize="off"
              autocorrect="off"
              spellcheck="false"
              placeholder="you@company.com"
              required
              aria-describedby="email-help"
              bind:value={email}
              bind:this={emailInput}
            />
          </div>
          <p class="field-help" id="email-help">
            We'll email you a 6-digit code and a link. Either one signs you in.
          </p>
          <button type="submit" class="btn btn-block btn-primary" disabled={busy}>{emailLabel}</button>
        </form>
      {:else}
        <!-- Step 2: the code. Same card, so the page never loses its place. -->
        <form onsubmit={onCodeSubmit} novalidate>
          <p class="auth-sent">{sentNote}</p>
          <div class="field">
            <label class="field-label" for="code">6-digit code <span class="req">*</span></label>
            <input
              class="input input-code"
              id="code"
              name="code"
              type="text"
              inputmode="numeric"
              autocomplete="one-time-code"
              autocapitalize="off"
              autocorrect="off"
              spellcheck="false"
              maxlength="6"
              pattern={'[0-9]{6}'}
              placeholder="123456"
              required
              aria-describedby="code-help"
              bind:value={code}
              bind:this={codeInput}
              oninput={onCodeInput}
            />
          </div>
          <p class="field-help" id="code-help">
            Or open the link in the same email — either one works.
          </p>
          <button type="submit" class="btn btn-block btn-primary" disabled={busy}>{codeLabel}</button>
          <div class="auth-actions">
            <button type="button" class="linkish" disabled={busy} onclick={showEmailStep}>
              Use a different email
            </button>
            <button type="button" class="linkish" disabled={busy || resendDisabled} onclick={onResend}>
              {resendLabel}
            </button>
          </div>
        </form>
      {/if}
  
      <!--
        Both regions stay in the DOM and in the accessibility tree at all times.
        Toggling `hidden` on a live region is the classic way to have an update
        announced to nobody; they collapse when empty instead.
      -->
      <p class="msg msg-ok" role="status" aria-live="polite">{statusMsg}</p>
      <p class="msg msg-err" role="alert" aria-live="assertive">{errorMsg}</p>
    </div>

    <p class="lede rise-fade" style="--reveal-i: 1">
      A document you and your AI agents work on together. They write; you comment on the exact
      words; they answer where you asked. Built by Unomena, free to use.
    </p>
  </section>

  <!-- 01 ------------------------------------------------------------------
       Three steps, three drawings, two lines each. MCP is folded into the
       first one rather than given a section of its own: it is how the agent
       gets in, not a separate feature. -->
  <section class="section">
    <header class="head" use:inview>
      <p class="meta-label head-tag" data-reveal>
        <span class="n">01</span> How it works
      </p>
      <h2 class="section-title" data-reveal style="--reveal-i: 1">
        Write. Comment. Answer<span class="dot">.</span>
      </h2>
      <p class="head-lede" data-reveal style="--reveal-i: 2">
        Nothing to install and nothing to configure in a dashboard. A document, an agent that can
        reach it, and a margin wide enough to argue in.
      </p>
    </header>

    <ol class="steps" use:inview>
      <li class="step" data-reveal>
        <figure class="shot"><LoginSketch kind="write" /></figure>
        <h3 class="step-title">You write it with an agent</h3>
        <p class="step-text">
          Any agent that speaks MCP can read, write and watch a document while you are looking at
          it. You mint its token yourself, under Settings → MCP once you are in.
        </p>
      </li>
      <li class="step" data-reveal style="--reveal-i: 1">
        <figure class="shot"><LoginSketch kind="comment" /></figure>
        <h3 class="step-title">You comment on the words</h3>
        <p class="step-text">
          Not a chat off to the side. Select a passage, the gap between two paragraphs, or a shape
          in a diagram, and leave the note there. @-mention a person and they get an email.
        </p>
      </li>
      <li class="step" data-reveal style="--reveal-i: 2">
        <figure class="shot"><LoginSketch kind="answer" /></figure>
        <h3 class="step-title">It answers where you asked</h3>
        <p class="step-text">
          The reply arrives in the thread you opened, against the passage it belongs to — so the
          question, the answer and the sentence they concern all stay in one place.
        </p>
      </li>
    </ol>
  </section>

  <!-- 02 ------------------------------------------------------------------
       The claim the brief is built on, said plainly and then explained. Only
       what is true today: free to use, and why. No promise about tomorrow. -->
  <section class="section">
    <header class="head" use:inview>
      <p class="meta-label head-tag" data-reveal>
        <span class="n">02</span> What it costs
      </p>
      <h2 class="section-title" data-reveal style="--reveal-i: 1">
        It costs nothing<span class="dot">.</span>
      </h2>
      <p class="head-lede" data-reveal style="--reveal-i: 2">
        Unomena built this for its own work and left the door open. No card, no trial, no ads.
      </p>
    </header>

    <div class="cost" use:inview>
      <p class="cost-lead" data-reveal>
        We built it for our own work. Having built it, there was no reason to keep it to ourselves.
      </p>
      <p class="cost-text" data-reveal style="--reveal-i: 1">
        Sign in with an email address and start. Folders, sharing and @-mentions are all there.
        Nothing on this page is a trial of anything.
      </p>
    </div>
  </section>

  <!-- Close: one line and a way back to the form. Not a second form -- a
       second one would be a second set of states to keep in step. -->
  <section class="close" use:inview>
    <p class="meta-label close-tag" data-reveal>Ready when you are</p>
    <h2 class="section-title" data-reveal style="--reveal-i: 1">
      Free, and one email away<span class="dot">.</span>
    </h2>
    <p class="close-actions" data-reveal style="--reveal-i: 2">
      <button type="button" class="meta-label link-grow to-form" onclick={toForm}>
        Start writing <span class="arr" aria-hidden="true">↑</span>
      </button>
    </p>
  </section>
</main>

<!--
  The dot is `position: fixed` and aria-hidden, so it sits outside <main>
  beside the footer rather than inside the document it flies over. It is only
  ever here: nothing else in this app imports it (TASK-047 §2).
-->
{#if Dot}
  <Dot />
{/if}

<!--
  unomena.com's footer, and the traveling dot's last stop -- its wordmark's
  full stop is the terminus of the journey that starts in the bar at the top.
  Rendered unconditionally: the dot may be absent, the footer never is.
-->
{#if Footer}
  <Footer />
{/if}

<style>
  /* =====================================================================
   * TASK-046. The product page.
   *
   * Scoped to this route on purpose. The app's global sheet keeps the FORM
   * (`.field`, `.input`, `.btn`, `.msg`, `.auth-or`) because Settings uses the
   * same controls; everything around it exists only here and would be a trap
   * for the next route that happened to use `[data-reveal]`.
   *
   * There is no `.wrap` in this app: unomena.com's container is spelled
   * `--shell-width` in app.css and is the identical expression, so every row
   * on this page starts at the same x as the wordmark in the bar above it.
   * ================================================================== */

  .product {
    width: var(--shell-width);
    margin-inline: auto;
    /*
     * Half what it was, and so is the footer's own top margin (SiteFooter).
     * The two stack -- this padding, then the footer's margin -- and together
     * they left 236px of nothing between "START WRITING" and the black band
     * at 1440. On unomena.com nothing adds bottom padding under its footer's
     * margin, so the site never doubles up; this page did. Halved on both
     * sides rather than zeroed on one, so the page still ends on some air in
     * the moment before the footer's chunk arrives.
     */
    padding-bottom: clamp(2rem, 4.5vw, 3.5rem);
  }

  /* The brand's full stop. Red, on every display heading, exactly as the
     site does it -- and never inherited by anything else. */
  .dot {
    color: var(--color-red);
  }

  /* --------------------------------------------------------------- hero */

  .hero {
    display: grid;
    /*
     * The site's hero pads clamp(4rem, 9vw, 8rem) at the top. This one cannot
     * afford it. At 390x844 the budget above the card is 68 (bar) + this
     * padding + the kicker + a three-line headline, and the submit button has
     * to land inside 844. At 1280x800 the same padding plus a headline at
     * --text-display-xl would reach 898px before the lede even started, which
     * is why the headline here is --text-display and the padding is this.
     */
    padding-block: clamp(2rem, 6vw, 5rem) clamp(3rem, 7vw, 5rem);
  }

  .kicker {
    color: var(--color-ink-soft);
    margin: 0 0 clamp(1.25rem, 3vw, 2rem);
  }

  .hero-title {
    font-size: var(--text-display);
    line-height: var(--text-display--line-height);
    letter-spacing: var(--text-display--letter-spacing);
    /*
     * 400, which is what unomena.com's display headings actually are: its
     * Tailwind preflight leaves headings unstyled, so they inherit the body
     * weight, and the light editorial grotesque at size IS the look. The
     * app's own 600 belongs to a reading surface, not to a page of the site.
     */
    font-weight: 400;
    /* balance, and no <br>: a hard break composed for 1280 is a widow at 390,
       and this headline has to survive both. */
    text-wrap: balance;
    margin: 0;
  }

  .lede {
    font-size: var(--text-lede);
    line-height: var(--text-lede--line-height);
    color: var(--color-ink-soft);
    max-width: 46ch;
    margin: clamp(1.5rem, 3vw, 2rem) 0 0;
    text-wrap: pretty;
  }

  /* ---------------------------------------------------------- the card */

  .card {
    background: var(--color-plinth);
    border: 1px solid var(--color-line);
    border-radius: 0.75rem;
    padding: clamp(1.25rem, 4vw, 1.75rem);
    margin-top: clamp(1.75rem, 4vw, 2.25rem);
    /* Wider than this and the email field becomes a runway. */
    max-width: 27rem;
  }

  .card-title {
    font-size: var(--text-title);
    line-height: var(--text-title--line-height);
    letter-spacing: var(--text-title--letter-spacing);
    font-weight: 400;
    margin: 0;
  }

  .card-note {
    margin: 0.625rem 0 1.5rem;
    color: var(--color-ink-soft);
    font-size: 0.9375rem;
    line-height: 1.5;
  }

  /*
   * Wide: the words on the left, the card on the right, one grid, both above
   * the fold. The card is pinned to the shell's right edge rather than
   * floating in the middle of its track -- every rule and every column on
   * this page terminates on the same two edges.
   *
   * Narrow: no areas at all, so the DOM order stands -- kicker, headline,
   * CARD, lede. That order is the whole argument of the page on a phone: you
   * are told what this is, then handed the way in, and the detail comes after.
   */
  /*
   * Wide: ONE grid for the whole page, not a two-column hero with full-width
   * sections under it.
   *
   * The card stays with you as you read (`position: sticky`), so every other
   * thing on the page has to keep to the left column -- otherwise the steps,
   * the drawings and the close would pass underneath it, which is precisely
   * what a person scrolling would see disappear. `.hero` becomes
   * `display: contents` so its parts join this grid directly; the card takes
   * column two for the WHOLE height (`grid-row: 1 / -1`), which is what gives
   * sticky something to stick within, all the way to the end of <main>.
   *
   * The hero's top padding moves here with it: `display: contents` leaves an
   * element's own box behind, padding and all.
   */
  @media (min-width: 60rem) {
    .product {
      display: grid;
      /*
       * The right column is the CARD's width, not a share of the page. As a
       * 0.9fr share it was far wider than the card's 27rem cap, and the card
       * sat at the far edge of it: 212px of nothing between the words and the
       * thing you type into, which read as two unrelated pages side by side.
       */
      grid-template-columns: minmax(0, 1fr) minmax(0, 25rem);
      /*
       * SIX explicit rows: the six things in the left column, in DOM order --
       * kicker, headline, lede, section 01, section 02, close.
       *
       * They have to be explicit for the card's `grid-row: 1 / -1` to mean
       * "all of them". Against implicit rows `-1` resolves to the last
       * explicit line, which is line 2, so the card occupied row one alone --
       * and being 351px tall it made row one 351px tall, which put a hole
       * between the kicker and the headline. Add a section here and this
       * count comes with it.
       */
      grid-template-rows: repeat(6, auto);
      column-gap: clamp(2rem, 3vw, 3rem);
      align-items: start;
      padding-block-start: clamp(2rem, 6vw, 5rem);
    }
    .hero {
      display: contents;
    }
    .kicker,
    .hero-title,
    .lede,
    .section,
    .close {
      grid-column: 1;
    }
    .card {
      grid-column: 2;
      grid-row: 1 / -1;
      align-self: start;
      justify-self: end;
      width: 100%;
      margin-top: 0;
      /* Under the sticky bar (68px), with the page's own breathing room. */
      position: sticky;
      top: calc(68px + clamp(1rem, 2vw, 1.75rem));
    }
  }

  /* ------------------------------------------------------- the sections */

  .section {
    padding-top: clamp(3rem, 7vw, 5rem);
  }

  /* SectionHead.svelte's shape: a rule across the full measure, the numbered
     mono tag under it, the display heading, then the lede. */
  .head {
    display: grid;
    gap: 1.5rem;
    padding-top: 1.25rem;
    border-top: 1px solid var(--color-line);
  }

  .head-tag {
    display: flex;
    gap: 1.25rem;
    color: var(--color-ink-soft);
    margin: 0;
  }

  .n {
    color: var(--color-ink-soft);
  }

  .section-title {
    font-size: var(--text-title);
    line-height: var(--text-title--line-height);
    letter-spacing: var(--text-title--letter-spacing);
    font-weight: 400;
    text-wrap: balance;
    max-width: 24ch;
    margin: 0;
  }

  .head-lede {
    font-size: var(--text-lede);
    line-height: var(--text-lede--line-height);
    color: var(--color-ink-soft);
    max-width: 44rem;
    margin: 0;
    text-wrap: pretty;
  }

  /* -------------------------------------------------------- 01, the steps */

  /*
   * Three across wherever three fit. The left column is narrower now that the
   * card holds the right one for the length of the page, so this is auto-fit
   * against a floor rather than a hard `repeat(3)`. The floor is 10rem, which
   * measures as three across from 1024 up (at 1024 the column is 531px and
   * three 160px tracks plus their gaps come to 520px); narrower than that they
   * wrap rather than crush.
   */
  .steps {
    list-style: none;
    margin: clamp(2rem, 4vw, 3rem) 0 0;
    padding: 0;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 10rem), 1fr));
    gap: clamp(1.25rem, 2.5vw, 2rem);
  }

  @media (max-width: 59.9rem) {
    .steps {
      grid-template-columns: 1fr;
    }
  }

  .step {
    margin: 0;
    /* A grid item's automatic minimum is its content, which here is a whole
       word plus the seat: without this the three tracks stop fitting long
       before the floor above says they should. */
    min-width: 0;
  }

  /*
   * The site's image seat: plinth, hairline, 4:3, clipped. The aspect ratio is
   * also what makes the drawing cost no layout -- the box is the box before
   * the SVG has drawn a single line, so nothing on the page moves as it
   * settles.
   */
  .shot {
    position: relative;
    margin: 0;
    aspect-ratio: 4 / 3;
    background: var(--color-plinth);
    border: 1px solid var(--color-line);
    border-radius: 0.5rem;
    overflow: hidden;
    display: grid;
    place-items: center;
    /* Cap the row to the content box, or the implicit row grows to the
       drawing's intrinsic height and top-anchors it. */
    grid-template-rows: minmax(0, 1fr);
    padding: clamp(1rem, 5%, 1.75rem);
    color: var(--color-ink);
  }

  .step-title {
    font-size: var(--text-lede);
    line-height: 1.25;
    letter-spacing: var(--text-title--letter-spacing);
    font-weight: 550;
    margin: 1.25rem 0 0.625rem;
    text-wrap: balance;
  }

  .step-text {
    color: var(--color-ink-soft);
    margin: 0;
    max-width: 40ch;
    text-wrap: pretty;
  }

  /* ------------------------------------------------------ 02, what it costs */

  .cost {
    display: grid;
    gap: clamp(1.5rem, 3vw, 2.5rem);
    margin-top: clamp(2rem, 4vw, 3rem);
  }

  @media (min-width: 60rem) {
    .cost {
      grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
      align-items: start;
    }
  }

  .cost-lead {
    font-size: var(--text-title);
    line-height: 1.3;
    letter-spacing: var(--text-title--letter-spacing);
    font-weight: 500;
    margin: 0;
    max-width: 30ch;
    text-wrap: pretty;
  }

  .cost-text {
    color: var(--color-ink-soft);
    font-size: var(--text-lede);
    line-height: var(--text-lede--line-height);
    margin: 0;
    max-width: 40ch;
    text-wrap: pretty;
  }

  /* ------------------------------------------------------------- the close */

  .close {
    padding-top: clamp(4rem, 8vw, 7rem);
    margin-top: clamp(3rem, 7vw, 5rem);
    border-top: 1px solid var(--color-line);
  }

  .close-tag {
    color: var(--color-red-deep);
    margin: 0 0 1.5rem;
  }

  .close-actions {
    margin: 2.5rem 0 0;
  }

  /*
   * A button, not an anchor: it moves the cursor into the form that is
   * already on this page, and `href="#"` would put a junk fragment in the
   * address bar for it. The suit is the site's quiet text link
   * (meta-label + link-grow, both global), with the button chrome removed --
   * `background-color`, not `background`, so link-grow's growing underline
   * survives the reset.
   */
  .to-form {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    border: 0;
    padding: 0.25rem 0;
    background-color: transparent;
    color: var(--color-ink);
    cursor: pointer;
  }

  .arr {
    display: inline-block;
  }

  @media (hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference) {
    .arr {
      transition: translate var(--duration-base) var(--ease-out-expo);
    }
    .to-form:hover .arr {
      translate: 0 -0.25em;
    }
  }

  /* ====================================================================
   * Motion
   *
   * The site's two mechanisms, brought across rather than imitated:
   *
   *   .rise / .rise-fade   page-head entrances, pure CSS, no JS wait
   *   [data-reveal]        the scroll choreography `use:inview` drives
   *
   * unomena.com gates every hidden state on `html.js`, set by an inline
   * script its CSP allows and ours does not. There is no gate here and none
   * is needed: this is a single-page app, so none of this markup exists
   * until the bundle has run.
   *
   * Both live INSIDE `prefers-reduced-motion: no-preference`. That is the
   * whole point: the app's global reduce block zeroes transition-duration
   * only, and .rise/.rise-fade are ANIMATIONS -- left outside, they would
   * still run their full 600ms for somebody who asked for less motion.
   * ================================================================= */

  @media (prefers-reduced-motion: no-preference) {
    .rise {
      animation: rise var(--duration-slow) var(--ease-out-expo) both;
    }

    .rise-fade {
      animation: rise-fade var(--duration-slow) var(--ease-out-expo) both;
      animation-delay: calc(var(--reveal-i, 0) * var(--stagger) * 2);
    }

    @keyframes rise {
      from {
        translate: 0 1.5rem;
      }
    }

    @keyframes rise-fade {
      from {
        translate: 0 1.25rem;
        opacity: 0;
      }
    }

    /* `:global()` because `.is-inview` is added by the action at runtime and
       the compiler cannot see it in the markup. Both halves stay fenced
       inside `.product`, so this cannot leak to another route. */
    .product :global([data-reveal]) {
      opacity: 0;
      translate: 0 1.25rem;
      transition:
        opacity var(--duration-slow) var(--ease-out-expo),
        translate var(--duration-slow) var(--ease-out-expo);
      transition-delay: calc(var(--reveal-i, 0) * var(--stagger));
    }

    .product :global(.is-inview [data-reveal]),
    .product :global([data-reveal].is-inview) {
      opacity: 1;
      translate: 0 0;
    }
  }

  /* The other half of the bargain: whatever happens to the observer, or to
     an element that is never scrolled to, the words are on the page. */
  @media (prefers-reduced-motion: reduce) {
    .product :global([data-reveal]) {
      opacity: 1;
      translate: none;
    }
  }
</style>
